import React, { useState, useEffect, useMemo } from "react";
import { Panel, Loader, Table, SelectPicker, InputNumber } from "rsuite";
import axios from "../../../utilities/axios";
import { MethodsUtils } from "../../../core/utils/MethodsUtils";
import dayjs from "dayjs";
import utc from "dayjs/plugin/utc";
import _ from "lodash";
dayjs.extend(utc)

const { Column, HeaderCell, Cell } = Table

type Props = {
    channels: string[];
    periods: any;
    searchTrigger: number;
}

interface SortData {
    sortColumn?: string;
    sortType?: 'asc' | 'desc';
}

const StoreMonthlyComparisonPanel: React.FC<Props> = ({ channels, periods, searchTrigger }) => {
    const [rows, setRows] = useState<any[]>([])
    const [months, setMonths] = useState<any[]>([])
    const [monthsToSearch, setMonthsToSearch] = useState<number>(6)
    const [loading, setLoading] = useState(false)
    const [sortData, setSortData] = useState<SortData>({})

    const updateTable = () => {
        setLoading(true)
        setTimeout(() => setLoading(false), 100)
    }

    const fetchData = useMemo(() => _.debounce(async (channels, periods, monthsToSearch) => {
        setLoading(true);

        try {
            const { data } = await axios.post('/api/v1/analytics/sales/store_monthly_comparison', { channels, periods, months: monthsToSearch })

            setRows(Array.isArray(data.data) ? data.data : []);
            setMonths(Array.isArray(data.months) ? data.months : []);
        } catch (err) {
            console.error(err);
            setRows([]);
            setMonths([]);
        } finally {
            setLoading(false);
        }
    }, 1000), []);

    useEffect(() => {
        if (searchTrigger > 0) {
            fetchData(channels, periods, monthsToSearch);
        }
    }, [searchTrigger, channels, periods, monthsToSearch]);

    const handleSortColumn = (sortColumn: string, sortType?: 'asc' | 'desc') => {
        setSortData({ sortColumn, sortType })
        const sortMonthIndex = months.findIndex((month) => dayjs(month).utc().format('MM/YYYY') == sortColumn)
        const sorted = [...rows].sort((a, b) => {
            const aVal = sortMonthIndex != -1 ? a.data[sortMonthIndex] : a[sortColumn]
            const bVal = sortMonthIndex != -1 ? b.data[sortMonthIndex] : b[sortColumn]
            if (typeof aVal === 'string') {
                return sortType === 'asc' ? aVal.localeCompare(bVal) : bVal.localeCompare(aVal)
            }
            return sortType === 'asc' ? aVal - bVal : bVal - aVal
        })
        setRows(sorted)
    }

    return (
        <Panel
            onEnter={updateTable}
            bordered
            style={{ background: 'white', borderColor: 'rgb(255, 140, 66)' }}
            header={
                <div style={{ display: 'flex', flexDirection: 'row', justifyContent: 'space-between', alignItems: 'center', paddingRight: 32 }}>
                    <strong>Comparativo Mensal por Loja</strong>
                    <div onClick={(e) => e.stopPropagation()}>
                        <InputNumber
                            value={monthsToSearch}
                            onChange={(v) => setMonthsToSearch(+v)}
                            min={6}
                            max={12}
                            step={1}
                            size="sm"
                            prefix="Últimos"
                            postfix="Meses"
                            style={{ width: 200 }}
                        />
                    </div>
                </div>
            }
            collapsible
        >

            {loading ? (
                <div style={{ padding: 24, display: 'flex', justifyContent: 'center' }}><Loader content="Carregando..." /></div>
            ) : rows.length === 0 ? (
                <div style={{ padding: 16 }}>Nenhum dado</div>
            ) : (
                <Table key={rows.length} data={rows} sortColumn={sortData.sortColumn} sortType={sortData.sortType} onSortColumn={handleSortColumn} autoHeight>
                    <Column width={300} sortable resizable>
                        <HeaderCell>LOJA</HeaderCell>
                        <Cell dataKey="store" />
                    </Column>

                    {months.map((month, index) => (
                        <Column width={120} sortable>
                            <HeaderCell>{dayjs(month).utc().format('MM/YYYY')}</HeaderCell>
                            <Cell dataKey={dayjs(month).utc().format('MM/YYYY')}>
                                {(rowData: any) => MethodsUtils.formatCurrency(rowData.data[index])}
                            </Cell>
                        </Column>
                    ))}
                </Table>
            )}
        </Panel>
    )
}

export default StoreMonthlyComparisonPanel
